/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import {
	ApplicationRef,
	ChangeDetectorRef,
	type ComponentRef,
	createComponent,
	Directive,
	ElementRef,
	EmbeddedViewRef,
	EnvironmentInjector,
	inject,
	Injector,
	input,
	type OnDestroy,
	type OnInit,
	output,
	Renderer2,
	Signal,
	signal,
	type Type,
	ViewContainerRef
} from "@angular/core";
import { Subject, throttleTime } from "rxjs";
import type { FloatingComponent } from "./floating-component";

@Directive({
	selector: "[cbFloatingElement]",

})
export abstract class FloatingElement<T extends FloatingComponent>
	implements OnInit, OnDestroy {
	private globalBuildListeners: (() => void)[] = [];
	private globalDestroyListeners: (() => void)[] = [];
	private renderer: Renderer2 = inject(Renderer2);
	private parentScrollListeners: (() => void)[] = [];

	/**
	 * The position of the floating element.
	 */
	public readonly position = input<"top" | "bottom" | "left" | "right">("left");

	public readonly trigger = input<"hover" | "focus" | "click" | "contextmenu" | "none">("none");

	public readonly closeOnInsideClick = input(false);

	public readonly distance = input(8);

	/**
	 * Triggered when the popover is being opened.
	 */
	public readonly popoverOpen = output<void>({ alias: 'popoverOpen' });

	/**
	 * Triggered when the popover is being closed.
	 */
	public readonly popoverClose = output({ alias: 'popoverClose' });

	protected readonly destroy$: Subject<void> = new Subject<void>();

	public readonly disabled = input<boolean>(false);

	public readonly matchParentWidth = input<boolean>(false);

	private readonly displayed = signal<boolean>(false);

	public clickPosition: { x: number; y: number } = { x: 0, y: 0 };

	private _leftPosition = signal(0);
	private _topPosition = signal(0);

	private _forcedPosition = signal<"top" | "bottom" | "left" | "right">("left");
	private _forcedAlignment = signal<"start" | "center" | "end">("center");

	protected componentRef: ComponentRef<T> | null = null;
	protected componentElement: HTMLElement | null = null;

	protected elementRef: ElementRef = inject(ElementRef);
	protected appRef: ApplicationRef = inject(ApplicationRef);
	protected vcr: ViewContainerRef = inject(ViewContainerRef);

	private onPageLayoutChange$ = new Subject<void>();

	private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

	protected component = signal<Type<T> | null>(null);

	private injector: Injector = inject(Injector);
	private environmentInjector: EnvironmentInjector = inject(EnvironmentInjector);

	ngOnInit() {
		this.attachGlobalBuildListeners();

		this.onPageLayoutChange$.pipe(
			throttleTime(100, undefined, { leading: true, trailing: true }),
		).subscribe(() => {
			if (this.displayed()) {
				this.updatePosition();
				this.updateComponentPosition();
			}
		});
	}

	// #region EVENTS

	//@HostListener("mouseenter", ["$event"])
	private onMouseEnter(event: MouseEvent): void {
		if (this.disabled()) return;

		if (this.trigger() === "hover") {
			this.buildFloating();
			this.cdr.detectChanges();
		}
	}

	//@HostListener("mouseleave", ["$event"])
	private onMouseLeave(event: MouseEvent): void {
		if (this.trigger() === "hover") this.destroyFloating();
	}

	//@HostListener("focus", ["$event"])
	private onFocus(event: MouseEvent): void {
		if (this.disabled()) return;

		if (this.trigger() === "focus") {
			this.buildFloating();
			this.cdr.detectChanges();
		}
	}

	//@HostListener("blur", ["$event"])
	private onBlur(event: MouseEvent): void {
		if (this.trigger() === "focus") this.destroyFloating();
	}

	//@HostListener("contextmenu", ["$event"])
	private onContextMenu(event: MouseEvent): void {
		if (!this.component) return;

		this.clickPosition = { x: event.clientX, y: event.clientY };

		if (this.trigger() === "contextmenu") {
			event.preventDefault();
			event.stopImmediatePropagation();

			if (!this.displayed()) {
				if (this.disabled()) return;
				this.buildFloating();
			} else {
				this.destroyFloating();
			}
		}
	}

	//@HostListener("click", ["$event"])
	private onClick(event: MouseEvent): void {
		if (!this.component) return;

		this.clickPosition = { x: event.clientX, y: event.clientY };

		const trigger = this.trigger();
		if (trigger === "contextmenu") {
			if (this.displayed()) {
				this.destroyFloating();
			}
		} else if (trigger === "click") {
			if (!this.displayed()) {
				if (this.disabled()) return;
				this.buildFloating();
			} else {
				this.destroyFloating();
			}
		}
	}

	//@HostListener("document:mousedown", ["$event"])
	private clickout(event: MouseEvent) {
		const trigger = this.trigger();
		if (trigger === "contextmenu" && event.button === 2) {
			event.preventDefault();
		}

		if (["click", "contextmenu"].includes(trigger) && this.displayed()) {
			if (!this.elementRef.nativeElement.contains(event.target)) {
				if (
					!this.componentRef ||
					!this.componentRef.location.nativeElement.contains(event.target)
				) {
					this.destroyFloating();
				}
			}
		}
	}

	//@HostListener("document:keydown", ["$event"])
	private onKeyDown(event: KeyboardEvent): void {
		if (this.trigger() === "contextmenu") {
			if (event.key === "Escape" && this.displayed()) {
				this.destroyFloating();
			}
		}
	}

	//@HostListener("window:scroll")
	//@HostListener("window:resize")
	private onPageLayoutChange(): void {
		this.onPageLayoutChange$.next();
	}

	private attachGlobalBuildListeners(): void {
		switch (this.trigger()) {
			case "hover":
				this.globalBuildListeners.push(
					this.renderer.listen(
						this.elementRef.nativeElement,
						"mouseenter",
						this.onMouseEnter.bind(this),
					),
				);
				this.globalBuildListeners.push(
					this.renderer.listen(
						this.elementRef.nativeElement,
						"mouseleave",
						this.onMouseLeave.bind(this),
					),
				);
				break;
			case "focus":
				this.globalBuildListeners.push(
					this.renderer.listen(
						this.elementRef.nativeElement,
						"focus",
						this.onFocus.bind(this),
					),
				);
				this.globalBuildListeners.push(
					this.renderer.listen(
						this.elementRef.nativeElement,
						"blur",
						this.onBlur.bind(this),
					),
				);
				break;
			case "contextmenu":
				this.globalBuildListeners.push(
					this.renderer.listen(
						this.elementRef.nativeElement,
						"contextmenu",
						this.onContextMenu.bind(this),
					),
				);
				break;
			case "click":
				this.globalBuildListeners.push(
					this.renderer.listen(
						this.elementRef.nativeElement,
						"click",
						this.onClick.bind(this),
					),
				);
				break;
		}
	}

	private attachGlobalDestroyListeners(): void {
		const trigger = this.trigger();
		if (trigger === "click" || trigger === "contextmenu") {
			this.globalDestroyListeners.push(
				this.renderer.listen("document", "mousedown", this.clickout.bind(this)),
			);
			this.globalDestroyListeners.push(
				this.renderer.listen("document", "keydown", this.onKeyDown.bind(this)),
			);
		}

		this.globalDestroyListeners.push(
			this.renderer.listen(
				"window",
				"scroll",
				this.onPageLayoutChange.bind(this),
			),
		);
		this.globalDestroyListeners.push(
			this.renderer.listen(
				"window",
				"resize",
				this.onPageLayoutChange.bind(this),
			),
		);
	}

	private attachParentScrollListeners(): void {
		let parent: HTMLElement | null = this.elementRef.nativeElement.parentElement;
		while (parent) {
			const listener = this.renderer.listen(
				parent,
				'scroll',
				this.onPageLayoutChange.bind(this)
			);
			this.parentScrollListeners.push(listener);
			parent = parent.parentElement;
		}
	}

	private detachParentScrollListeners(): void {
		for (const unlistener of this.parentScrollListeners) {
			unlistener();
		}
		this.parentScrollListeners = [];
	}

	private unlistenBuildListeners(): void {
		for (const unlistener of this.globalBuildListeners) {
			unlistener();
		}
	}

	private unlistenDestroyListeners(): void {
		for (const unlistener of this.globalDestroyListeners) {
			unlistener();
		}
	}

	// #region GETTERS

	/**
	 * The left position of the floating.
	 */
	public get leftPosition(): Signal<number> {
		return this._leftPosition.asReadonly();
	}

	/**
	 * The top position of the floating.
	 */
	public get topPosition(): Signal<number> {
		return this._topPosition.asReadonly();
	}

	/**
	 * The forced position of the floating.
	 */
	public get forcedPosition(): Signal<"top" | "bottom" | "left" | "right"> {
		return this._forcedPosition.asReadonly();
	}

	/**
	 * The forced alignment of the floating.
	 */
	public get forcedAlignment(): Signal<"start" | "center" | "end"> {
		return this._forcedAlignment.asReadonly();
	}

	// #region BUILD
	public buildFloating(): void {
		if (this.componentRef === null && this.component()) {
			this.displayed.set(true);

			this.componentRef = createComponent(this.component()!, {
				environmentInjector: this.environmentInjector,
			});
			this.appRef.attachView(this.componentRef.hostView);

			this.componentElement = (this.componentRef.hostView as EmbeddedViewRef<any>)
				.rootNodes[0] as HTMLElement;

			this.componentElement!.style.visibility = 'hidden';

			document.body.appendChild(this.componentElement);

			this.setComponentProperties();
			this.updateComponentPosition();

			this.attachParentScrollListeners();

			this.loadPosition();
			this.popoverOpen.emit();

			this.attachGlobalDestroyListeners();
		}
	}

	public open(): void {
		if (this.displayed()) return;
		this.buildFloating();
	}

	public close(): void {
		this.destroyFloating();
	}

	private destroyFloating(): void {
		this.displayed.set(false);
		this.popoverClose.emit();

		this.detachParentScrollListeners();

		this.destroy();
		this.cdr.detectChanges();
	}

	public abstract setComponentProperties(): void;

	// #region POSITIONING

	private loadPosition(): void {
		setTimeout(() => {
			if (this.componentElement) {
				this.updateSize();
				this.updatePosition();
				this.updateComponentPosition();

				this.componentElement!.style.visibility = 'visible';
			}
		});
	}

	/**
	 * Updates the position of the component.
	 * @returns
	 */
	private updateComponentPosition(): void {
		if (this.componentRef === null || !this.componentElement) return;

		this.componentRef.setInput("topPosition", this.topPosition());
		this.componentRef.setInput("leftPosition", this.leftPosition());

		this.componentRef.setInput("position", this.forcedPosition());
		this.componentRef.setInput("alignment", this.forcedAlignment());
	}

	private updateSize(): void {
		if (this.componentElement === null) return;

		if (this.matchParentWidth() && this.componentElement.firstChild) {
			const firstChild = this.componentElement.firstChild as HTMLElement;
			firstChild.style.width = `${this.elementRef.nativeElement.offsetWidth}px`;
		}
	}

	/**
	 * Calculates the measurements of the floating and the element (bounding client rect).
	 * @returns The measurements of the floating and the element.
	 */
	private getMeasurements(): {
		windowWidth: number;
		windowHeight: number;
		floating: DOMRect;
		element: {
			left: number;
			right: number;
			top: number;
			bottom: number;
			width: number;
			height: number;
		};
		delta: number;
	} {
		const windowWidth = window.innerWidth;
		const windowHeight = window.innerHeight;

		if (!this.componentElement)
			throw new Error("Component element is not defined");

		const floating = this.componentElement.children[0].getBoundingClientRect();
		const element = this.elementRef.nativeElement.getBoundingClientRect();

		let left = 0;
		let right = 0;
		let top = 0;
		let bottom = 0;
		let width = 0;
		let height = 0;

		if (this.trigger() === "contextmenu") {
			left = this.clickPosition.x;
			right = this.clickPosition.x;
			top = this.clickPosition.y;
			bottom = this.clickPosition.y;
			width = 0;
			height = 0;
		} else {
			left = element.left;
			right = element.right;
			top = element.top;
			bottom = element.bottom;
			width = element.width;
			height = element.height;
		}

		return {
			windowWidth,
			windowHeight,
			floating: floating,
			element: {
				left,
				right,
				top,
				bottom,
				width,
				height,
			},
			delta: this.distance(),
		};
	}

	/**
	 * Updates the forced position of the floating.
	 */
	private updateForcedPosition(): void {
		const { windowWidth, windowHeight, floating, element, delta } =
			this.getMeasurements();

		switch (this.position()) {
			case "left": {
				const leftPosition = element.left - floating.width - delta;

				if (leftPosition < 0) {
					this._forcedPosition.set("right");
				} else {
					this._forcedPosition.set("left");
				}
				break;
			}
			case "right": {
				const rightPosition = element.right + floating.width + delta;

				if (rightPosition > windowWidth) {
					this._forcedPosition.set("left");
				} else {
					this._forcedPosition.set("right");
				}
				break;
			}
			case "top": {
				const topPosition = element.top - floating.height - delta;

				if (topPosition < 0) {
					this._forcedPosition.set("bottom");
				} else {
					this._forcedPosition.set("top");
				}
				break;
			}
			case "bottom": {
				const bottomPosition = element.bottom + floating.height + delta;

				if (bottomPosition > windowHeight) {
					this._forcedPosition.set("top");
				} else {
					this._forcedPosition.set("bottom");
				}
				break;
			}
		}
	}

	/**
	 * Updates the position of the floating.
	 */
	private updatePosition(): void {
		const { windowWidth, windowHeight, floating, element, delta } =
			this.getMeasurements();

		this.updateForcedPosition();
		switch (this._forcedPosition()) {
			case "left":
				this._leftPosition.set(element.left - floating.width - delta);
				break;
			case "right":
				this._leftPosition.set(element.right + delta);
				break;
			case "top":
				this._topPosition.set(element.top - floating.height - delta);
				break;
			case "bottom":
				this._topPosition.set(element.bottom + delta);
				break;
		}

		this.updateAlignment();

	}

	/**
	 * Updates the alignment of the floating.
	 */
	private updateAlignment(): void {
		const { windowWidth, windowHeight, floating, element, delta } =
			this.getMeasurements();

		switch (this._forcedPosition()) {
			case "left":
			case "right": {
				let top = element.top + element.height / 2 - floating.height / 2;
				this._forcedAlignment.set("center");

				if (top < delta) {
					top = delta;
					this._forcedAlignment.set("start");
				}

				if (top + floating.height > windowHeight - delta) {
					top = windowHeight - delta - floating.height;
					this._forcedAlignment.set("end");
				}

				this._topPosition.set(top);
				break;
			}
			case "top":
			case "bottom": {
				let left = element.left + element.width / 2 - floating.width / 2;
				this._forcedAlignment.set("center");

				if (left < delta) {
					left = delta;
					this._forcedAlignment.set("start");
				}

				if (left + floating.width > windowWidth - delta) {
					left = windowWidth - delta - floating.width;
					this._forcedAlignment.set("end");
				}

				this._leftPosition.set(left);
				break;
			}
		}
	}

	ngOnDestroy(): void {
		// Unsubscribe from all listeners
		this.unlistenBuildListeners();
		this.destroy();
	}

	destroy(): void {
		if (this.componentRef !== null) {
			this.appRef.detachView(this.componentRef.hostView);
			this.componentRef.destroy();
			this.componentRef = null;
		}

		// Unsubscribe from all listeners
		this.unlistenDestroyListeners();

		this.destroy$.next();
		this.destroy$.complete();
	}
}
